import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Paper, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import {
	canPromptInstall,
	dismissInstallBanner,
	installBannerDismissed,
	installBannerRequested,
	manualInstallSteps,
	onInstallChange,
	promptInstall,
	shouldOfferInstall,
} from "../../utils/installApp";

/** Re-renders the caller whenever the browser offers or withdraws the install. */
function useInstallState(): void {
	const [, setTick] = useState(0);
	useEffect(() => onInstallChange(() => setTick((n) => n + 1)), []);
}

const HowToInstall: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
	<Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
		<DialogTitle sx={{ fontWeight: 700 }}>Install the Styola Orders app</DialogTitle>
		<DialogContent>
			<Typography sx={{ fontSize: 15, color: "#333" }}>{manualInstallSteps()}</Typography>
			<Typography sx={{ fontSize: 13, color: "#666", mt: 1.5 }}>
				The app shows your orders and shipping, and can alert you about new orders, pickups and deliveries.
			</Typography>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose} sx={{ textTransform: "none", color: "#111" }}>
				OK
			</Button>
		</DialogActions>
	</Dialog>
);

/** Runs the install: the browser's own dialog when it is on offer, otherwise the steps. */
export function useInstallAction(): { install: () => Promise<void>; helpDialog: React.ReactNode } {
	const [helpOpen, setHelpOpen] = useState(false);
	const install = async () => {
		// If the browser cannot open its own dialog, show the steps instead of doing nothing.
		if ((await promptInstall()) === "unavailable") setHelpOpen(true);
	};
	return { install, helpDialog: <HowToInstall open={helpOpen} onClose={() => setHelpOpen(false)} /> };
}

/**
 * The bar at the bottom of the page inviting the merchant to install the app.
 * Shown as soon as the visitor taps "Start Selling". If the browser is ready to
 * install, the button opens its install dialog; otherwise (iPhone, or a first
 * visit where Chrome is not ready yet) it shows the steps. Never inside the
 * installed app, and not for a week after "Not now".
 */
export const InstallBanner: React.FC = () => {
	useInstallState();
	const [hidden, setHidden] = useState(installBannerDismissed());
	const { install, helpDialog } = useInstallAction();

	const offer = shouldOfferInstall() && installBannerRequested() && !hidden;

	const notNow = () => {
		dismissInstallBanner();
		setHidden(true);
	};

	return (
		<>
			{offer && (
				<Paper
					elevation={12}
					role="dialog"
					aria-label="Install the Styola Orders app"
					sx={{
						position: "fixed",
						left: { xs: 8, sm: "auto" },
						right: { xs: 8, sm: 24 },
						bottom: { xs: "calc(8px + env(safe-area-inset-bottom))", sm: 24 },
						zIndex: 1400,
						maxWidth: { sm: 400 },
						p: 2,
						borderRadius: 2,
						bgcolor: "#111",
						color: "#fff",
					}}
				>
					<Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
						<InstallMobileIcon sx={{ color: "#E09145", mt: 0.25 }} />
						<Box sx={{ flex: 1 }}>
							<Typography sx={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Install the Styola Orders app</Typography>
							<Typography sx={{ fontSize: 13, color: "#ddd", mt: 0.5 }}>
								Track orders and shipping, and get alerts for new orders, pickups and deliveries.
							</Typography>
						</Box>
						<IconButton aria-label="Close" size="small" onClick={notNow} sx={{ color: "#bbb", mt: -0.5, mr: -0.5 }}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>
					<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
						<Button onClick={notNow} sx={{ textTransform: "none", color: "#ddd" }}>
							Not now
						</Button>
						<Button
							onClick={install}
							variant="contained"
							sx={{ textTransform: "none", bgcolor: "#E09145", color: "#111", fontWeight: 700, "&:hover": { bgcolor: "#c97f38" } }}
						>
							{canPromptInstall() ? "Install" : "How to install"}
						</Button>
					</Box>
				</Paper>
			)}
			{helpDialog}
		</>
	);
};
